/**
 * PastDueBanner — shown on the patient dashboard when a subscription payment
 * failed or the plan was paused for non-payment. Drives the patient to update
 * their card so billing + shipments resume.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

export default function PastDueBanner() {
  const [sub, setSub] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await API.get("/subscriptions/mine");
        if (!alive) return;
        const flagged = (Array.isArray(data) ? data : []).find(
          (s) => s.payment_failed || s.paused_reason === "past_due_grace_expired"
        );
        setSub(flagged || null);
      } catch {
        /* silent */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!sub) return null;
  const paused = sub.paused_reason === "past_due_grace_expired" || sub.status === "PAUSED";

  return (
    <div
      className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
      data-testid="past-due-banner"
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-rose-600" />
        </div>
        <div>
          <p className="font-semibold text-rose-900">
            {paused ? "Your subscription is paused" : "Your payment didn't go through"}
          </p>
          <p className="text-sm text-rose-700/90 mt-0.5">
            {paused
              ? "We couldn't process payment, so your plan is paused and your next shipment is on hold. Update your card to resume."
              : "Please update your payment card to keep your subscription active — your next shipment is on hold until it's paid."}
          </p>
        </div>
      </div>
      <Button
        className="rounded-full bg-rose-600 hover:bg-rose-700 text-white whitespace-nowrap"
        onClick={() => navigate("/profile")}
        data-testid="past-due-update-card-btn"
      >
        Update card to resume
      </Button>
    </div>
  );
}
